import { useState, useEffect, useCallback } from "react";
import { useAppTheme, ACCENT, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";

const API = "/api";
const PAGE = 100;

const ACTION_LABELS = {
  "pm.materials": "Materjalide plaan muudetud",
  "pm.rates": "Masinate tunnihinnad muudetud",
  "pm.offer_upload": "Pakkumine üles laetud",
  "pm.offer_rows": "Pakkumise read muudetud",
  "pm.offer_calc": "Ühikhinna kalkulatsioon muudetud",
  "site.create": "Objekt loodud",
  "site.update": "Objekti seaded muudetud",
  "auth.login": "Sisselogimine",
  "auth.login_failed": "Ebaõnnestunud sisselogimine",
  "auth.logout": "Väljalogimine",
  "user.create": "Kasutaja lisatud",
  "user.update": "Kasutaja muudetud",
  "user.delete": "Kasutaja kustutatud",
  "delivery.create": "Koorem logitud",
  "delivery.delete": "Koorem kustutatud",
  "delivery.update_reg": "Koorma reg nr muudetud",
  "machine_hours.create": "Masinatunnid logitud",
  "machine_hours.update": "Masinatunnid muudetud",
  "machine_hours.delete": "Masinatunnid kustutatud",
  "timer.start": "Taimer käivitatud",
  "timer.stop": "Taimer peatatud",
  "supplier_delivery.create": "Tarneleht lisatud",
  "supplier_delivery.import": "Tarnelehtede import",
  "waybiller.import": "Waybilleri import",
  "ingest.run": "Ingestorid käivitatud käsitsi",
  "sms.delivery_failed": "SMS jäi kohale toimetamata",
  "sms.fallback": "SMS saadeti varupakkuja kaudu",
  "sms.test": "SMS testsõnum",
  "verston.import": "Verstoni import",
  "waybiller.rename_material": "Materjal ümber nimetatud",
  "duplicate.dismiss": "Duplikaadi lipp tühistatud",
  "fleet.add": "Masin objektile lisatud",
  "fleet.remove": "Masin objektilt eemaldatud",
  "worker_hours.import": "Töötundide import",
  "daily_report.start": "Päevaaruanne alustatud",
  "daily_report.worker_count": "Päevaaruanne: töötajad",
  "daily_report.fleet": "Päevaaruanne: tehnika",
  "daily_report.work_done": "Päevaaruanne: tehtud tööd",
  "daily_report.materials": "Päevaaruanne: materjalid",
  "daily_report.complete": "Päevaaruanne esitatud",
};

const GROUP_LABELS = {
  auth: "Sisselogimised", user: "Kasutajad", delivery: "Koormad", machine_hours: "Masinatunnid", timer: "Taimerid",
  supplier_delivery: "Tarnelehed", waybiller: "Waybiller", verston: "Verston", duplicate: "Duplikaadid",
  fleet: "Tehnika", worker_hours: "Töötunnid", daily_report: "Päevaaruanded",
};

const ROLE_LABELS = { admin: "Admin", site_lead: "Objektijuht", operator: "Operaator" };

const FIELD_LABELS = {
  reg: "reg nr", material: "materjal", site: "objekt", sites: "objektid", name: "nimi", role: "roll",
  active: "aktiivne", email: "e-post", phone_number: "telefon", hours: "tunnid", machine_name: "masin",
  machine_type: "masina tüüp", operator_name: "operaator", work_date: "kuupäev", description: "kirjeldus",
  logged_at: "aeg", logged_by: "logija", vehicle_type: "sõiduk", filename: "fail", inserted: "lisatud",
  updated: "uuendatud", skipped: "vahele jäetud", errors: "vigu", identifier: "kasutajatunnus", reason: "põhjus",
  method: "viis", via: "kanal", rows: "ridu", started_at: "algus", value: "väärtus",
};

const REASON_LABELS = {
  unknown_user: "tundmatu kasutaja", deactivated: "deaktiveeritud", wrong_code: "vale kood",
  expired: "kood aegunud", too_many_attempts: "liiga palju katseid", no_active_user: "kasutaja pole aktiivne",
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "jah" : "ei";
  if (Array.isArray(v)) return v.length ? v.map(fmt).join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function Detail({ e, t }) {
  const d = e.detail;
  if (!d) return null;
  const line = (k, content) => (
    <div key={k}><span style={{ color: t.muted }}>{FIELD_LABELS[k] || k}:</span> {content}</div>
  );
  const rows = [];
  if (d.changes) {
    Object.entries(d.changes).forEach(([k, c]) => rows.push(line(k,
      <><span style={{ textDecoration: "line-through", color: t.muted }}>{fmt(c.before)}</span> → <b>{fmt(c.after)}</b></>)));
  } else if (d.before && d.after && typeof d.before === "object" && typeof d.after === "object") {
    Object.keys(d.after).forEach(k => rows.push(line(k,
      <><span style={{ textDecoration: "line-through", color: t.muted }}>{fmt(d.before[k])}</span> → <b>{fmt(d.after[k])}</b></>)));
  } else {
    const shown = d.before || d.after;
    if (shown && typeof shown === "object") {
      Object.entries(shown).filter(([k]) => k !== "id").forEach(([k, v]) => rows.push(line(k, fmt(v))));
    }
  }
  Object.entries(d)
    .filter(([k]) => !["before", "after", "changes", "ids", "client_ref", "first_errors"].includes(k))
    .forEach(([k, v]) => rows.push(line(k, k === "reason" ? (REASON_LABELS[v] || v) : fmt(v))));
  (d.first_errors || []).forEach((err, i) => rows.push(<div key={`err${i}`} style={{ color: "#c53030" }}>{err}</div>));
  return <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>{rows}</div>;
}

export default function AuditLog() {
  const { t } = useAppTheme();
  const { user } = useAuth();
  const mobile = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ user_id: "", action: "", date_from: "", date_to: "" });

  const load = useCallback((offset) => {
    const q = new URLSearchParams({ limit: PAGE, offset });
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    setLoading(true);
    fetch(`${API}/admin/audit?${q}`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => {
        setEntries(prev => (offset ? [...prev, ...d.entries] : d.entries));
        setTotal(d.total);
        setUsers(d.users);
        setActions(d.actions);
        setError(null);
      })
      .catch(() => setError("Tegevuslogi laadimine ebaõnnestus"))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { if (user?.role === "admin") load(0); }, [load, user]);

  if (user?.role !== "admin") {
    return <div style={{ padding: 40, textAlign: "center", color: t.muted }}>Tegevuslogi näeb ainult admin</div>;
  }

  const groups = [...new Set(actions.map(a => a.split(".")[0]))];
  const set = (k) => (ev) => setFilters(f => ({ ...f, [k]: ev.target.value }));
  const inputStyle = {
    background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px",
    fontSize: mobile ? 12 : 13, color: t.text, outline: "none",
  };
  const label = (text, control) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: t.muted, fontWeight: 600 }}>
      {text}{control}
    </label>
  );

  return (
    <div style={{ padding: mobile ? "16px 12px" : "24px 20px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 800, color: t.text }}>Tegevuslogi</div>
        <div style={{ fontSize: mobile ? 12 : 13, color: t.muted, marginTop: 4 }}>
          Kes mida LogSite'is tegi, uuemad eespool. {loading && !entries.length ? "Laen..." : `${total} kirjet`}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        {label("Kasutaja",
          <select value={filters.user_id} onChange={set("user_id")} style={inputStyle}>
            <option value="">Kõik kasutajad</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>)}
        {label("Tegevus",
          <select value={filters.action} onChange={set("action")} style={inputStyle}>
            <option value="">Kõik tegevused</option>
            {groups.map(g => (
              <optgroup key={g} label={GROUP_LABELS[g] || g}>
                <option value={g}>Kõik: {GROUP_LABELS[g] || g}</option>
                {actions.filter(a => a.split(".")[0] === g).map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
              </optgroup>
            ))}
          </select>)}
        {label("Alates", <input type="date" value={filters.date_from} onChange={set("date_from")} style={inputStyle} />)}
        {label("Kuni", <input type="date" value={filters.date_to} onChange={set("date_to")} style={inputStyle} />)}
        {Object.values(filters).some(Boolean) && (
          <button onClick={() => setFilters({ user_id: "", action: "", date_from: "", date_to: "" })} style={{
            all: "unset", cursor: "pointer", fontSize: 12, color: ACCENT, padding: "8px 4px", fontWeight: 600,
          }}>Tühjenda filtrid</button>
        )}
      </div>

      {error && <div style={{ color: "#c53030", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {!loading && !entries.length ? (
        <div style={{ textAlign: "center", padding: 40, color: t.muted }}>Kirjeid pole</div>
      ) : (
        <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
          {entries.map((e, i) => (
            <div key={e.id} style={{
              display: "grid", gridTemplateColumns: mobile ? "1fr" : "150px 180px 1fr", gap: mobile ? 4 : 14,
              padding: mobile ? "10px 12px" : "10px 16px", borderTop: i ? `1px solid ${t.rowBorder}` : "none",
              fontSize: mobile ? 12 : 13, color: t.text,
            }}>
              <div style={{ color: t.muted, fontVariantNumeric: "tabular-nums" }}>{e.at_local}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{e.user_name || "—"}</div>
                <div style={{ fontSize: 11, color: t.mutedLight }}>
                  {[ROLE_LABELS[e.role] || e.role, e.ip].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: e.action.endsWith("delete") || e.action === "auth.login_failed" ? "#c53030" : t.text }}>
                  {ACTION_LABELS[e.action] || e.action}
                  {(e.site || e.entity_id) && (
                    <span style={{ fontWeight: 400, color: t.muted }}>
                      {" · "}{[e.site, e.entity_id && `#${e.entity_id}`].filter(Boolean).join(" ")}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: mobile ? 11 : 12, marginTop: 2 }}><Detail e={e} t={t} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length < total && (
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button disabled={loading} onClick={() => load(entries.length)} style={{
            all: "unset", cursor: "pointer", fontSize: 13, fontWeight: 600, color: ACCENT,
            padding: "8px 18px", borderRadius: 8, border: `1px solid ${ACCENT}44`,
          }}>{loading ? "Laen..." : `Näita veel (${total - entries.length})`}</button>
        </div>
      )}
    </div>
  );
}
